import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import { hierarchicalLocationsAPI } from '../services/api';
import {
    HierarchicalLocation,
    LocationType,
    CreateDepartmentDto,
    CreateFloorDto,
    BulkCreateRoomsRangeDto
} from '../types';
import './HierarchicalQRWizard.css';

interface WizardStep {
    number: number;
    title: string;
    description: string;
}

const WIZARD_STEPS: WizardStep[] = [
    { number: 1, title: 'Departman Seç', description: 'Mevcut departmanı seçin veya yeni oluşturun' },
    { number: 2, title: 'Kat Seç', description: 'Katı seçin veya yeni kat ekleyin' },
    { number: 3, title: 'Oda QR Kodları', description: 'Odalar için QR kodları oluşturun' }
];

const HierarchicalQRWizard: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [departments, setDepartments] = useState<HierarchicalLocation[]>([]);
    const [floors, setFloors] = useState<HierarchicalLocation[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<HierarchicalLocation | null>(null);
    const [selectedFloor, setSelectedFloor] = useState<HierarchicalLocation | null>(null);
    const [loading, setLoading] = useState(false);

    // Form states
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [newFloorNumber, setNewFloorNumber] = useState('');
    const [roomStartNumber, setRoomStartNumber] = useState('1');
    const [roomEndNumber, setRoomEndNumber] = useState('10');
    const [createdRooms, setCreatedRooms] = useState<HierarchicalLocation[]>([]);
    const [generatedQRs, setGeneratedQRs] = useState<{ [key: string]: string }>({});

    // Generate QR Code URL
    const generateQRCodeUrl = (locationName: string) => {
        const encodedLocation = encodeURIComponent(locationName);
        return `${window.location.origin}/feedback-form?location=${encodedLocation}&source=qr`;
    };

    // Generate QR Code on canvas
    const generateQRCodeCanvas = async (url: string, location: string, roomId: number) => {
        try {
            const canvas = document.getElementById(`qr-room-${roomId}`) as HTMLCanvasElement;
            if (canvas) {
                await QRCode.toCanvas(canvas, url, {
                    width: 200,
                    margin: 2,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF'
                    }
                });

                const dataUrl = canvas.toDataURL();
                setGeneratedQRs(prev => ({
                    ...prev,
                    [location]: dataUrl
                }));
            }
        } catch (error) {
            console.error('QR kod oluşturma hatası:', error);
        }
    };

    // Download single QR code
    const downloadQRCode = (location: string, qrCode: string) => {
        const dataUrl = generatedQRs[location];
        if (dataUrl) {
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `${location.replace(/\s+/g, '-')}-${qrCode}.png`;
            link.click();
        }
    };

    // Download all QR codes as ZIP
    const downloadAllQRCodes = async () => {
        try {
            const zip = new JSZip();

            // Create folder name from floor
            const folderName = selectedFloor?.name.replace(/\s+/g, '-') || 'QR-Kodlari';
            const folder = zip.folder(folderName);

            if (!folder) {
                alert('ZIP klasörü oluşturulamadı!');
                return;
            }

            // Add each QR code to the ZIP
            createdRooms.forEach((room) => {
                const dataUrl = generatedQRs[room.name];
                if (dataUrl) {
                    // Convert data URL to base64
                    const base64Data = dataUrl.split(',')[1];
                    const fileName = `${room.name.replace(/\s+/g, '-')}-${room.qrCode}.png`;
                    folder.file(fileName, base64Data, { base64: true });
                }
            });

            // Generate ZIP file
            const content = await zip.generateAsync({ type: 'blob' });

            // Download ZIP file
            const link = document.createElement('a');
            link.href = URL.createObjectURL(content);
            link.download = `${folderName}.zip`;
            link.click();

            // Clean up
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('ZIP oluşturma hatası:', error);
            alert('QR kodları indirilirken hata oluştu!');
        }
    };

    // Print QR code
    const printQRCode = (location: string, qrCode: string) => {
        const dataUrl = generatedQRs[location];
        if (dataUrl) {
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(`
                    <html>
                        <head>
                            <title>QR Kod - ${location}</title>
                            <style>
                                body { text-align: center; padding: 20px; font-family: Arial, sans-serif; }
                                h2 { color: #333; margin-bottom: 20px; }
                                img { max-width: 300px; border: 2px solid #eee; border-radius: 8px; padding: 10px; }
                                p { color: #666; margin-top: 10px; font-family: monospace; }
                            </style>
                        </head>
                        <body>
                            <h2>${location}</h2>
                            <img src="${dataUrl}" />
                            <p>QR Kod: ${qrCode}</p>
                        </body>
                    </html>
                `);
                printWindow.document.close();
                printWindow.print();
            }
        }
    };

    // Load departments on mount
    useEffect(() => {
        loadDepartments();
    }, []);

    // Load floors when department is selected
    useEffect(() => {
        if (selectedDepartment) {
            loadFloors(selectedDepartment.id);
        }
    }, [selectedDepartment]);

    // Generate QR codes when rooms are created
    useEffect(() => {
        if (createdRooms.length > 0) {
            createdRooms.forEach(room => {
                const url = generateQRCodeUrl(room.name);
                setTimeout(() => {
                    generateQRCodeCanvas(url, room.name, room.id);
                }, 100);
            });
        }
    }, [createdRooms]);

    const loadDepartments = async () => {
        try {
            setLoading(true);
            const response = await hierarchicalLocationsAPI.getHierarchy();
            setDepartments(response.data);
        } catch (error) {
            console.error('Departmanlar yüklenemedi:', error);
            alert('Departmanlar yüklenirken hata oluştu!');
        } finally {
            setLoading(false);
        }
    };

    const loadFloors = async (departmentId: number) => {
        try {
            setLoading(true);
            const response = await hierarchicalLocationsAPI.getDepartmentFloors(departmentId);
            setFloors(response.data);
        } catch (error) {
            console.error('Katlar yüklenemedi:', error);
            alert('Katlar yüklenirken hata oluştu!');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateDepartment = async () => {
        if (!newDepartmentName.trim()) {
            alert('Lütfen departman adı girin!');
            return;
        }

        try {
            setLoading(true);
            const dto: CreateDepartmentDto = {
                name: newDepartmentName.trim(),
                description: `${newDepartmentName} departmanı`
            };

            const response = await hierarchicalLocationsAPI.createDepartment(dto);
            setDepartments([...departments, response.data]);
            setNewDepartmentName('');
            alert('Departman başarıyla oluşturuldu!');
        } catch (error: any) {
            console.error('Departman oluşturulamadı:', error);
            alert(error.response?.data || 'Departman oluşturulamadı!');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectDepartment = (dept: HierarchicalLocation) => {
        setSelectedDepartment(dept);
        setSelectedFloor(null);
        setCreatedRooms([]);
        setCurrentStep(2);
    };

    const handleCreateFloor = async () => {
        if (!selectedDepartment) {
            alert('Önce bir departman seçin!');
            return;
        }

        if (!newFloorNumber.trim()) {
            alert('Lütfen kat numarası girin!');
            return;
        }

        try {
            setLoading(true);
            const dto: CreateFloorDto = {
                departmentId: selectedDepartment.id,
                floorNumber: parseInt(newFloorNumber),
                description: `${selectedDepartment.name} - ${newFloorNumber}. Kat`
            };

            const response = await hierarchicalLocationsAPI.createFloor(dto);
            setFloors([...floors, response.data]);
            setNewFloorNumber('');
            alert('Kat başarıyla oluşturuldu!');
        } catch (error: any) {
            console.error('Kat oluşturulamadı:', error);
            alert(error.response?.data || 'Kat oluşturulamadı!');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectFloor = (floor: HierarchicalLocation) => {
        setSelectedFloor(floor);
        setCreatedRooms([]);
        setGeneratedQRs({});
        setCurrentStep(3);
    };

    const handleViewFloorQRs = async (floor: HierarchicalLocation) => {
        try {
            setLoading(true);
            const response = await hierarchicalLocationsAPI.getFloorRooms(floor.id);
            if (response.data.length > 0) {
                setSelectedFloor(floor);
                setCreatedRooms(response.data);
                setCurrentStep(3);
            } else {
                alert('Bu katta henüz oda bulunmuyor.');
            }
        } catch (error) {
            console.error('Odalar yüklenemedi:', error);
            alert('Odalar yüklenirken hata oluştu!');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRooms = async () => {
        if (!selectedFloor) {
            alert('Önce bir kat seçin!');
            return;
        }

        const start = parseInt(roomStartNumber);
        const end = parseInt(roomEndNumber);

        if (isNaN(start) || isNaN(end) || start < 1 || end < start) {
            alert('Geçerli bir oda aralığı girin! (Başlangıç <= Bitiş)');
            return;
        }

        if (end - start > 100) {
            alert('Bir seferde en fazla 100 oda oluşturabilirsiniz!');
            return;
        }

        try {
            setLoading(true);
            const dto: BulkCreateRoomsRangeDto = {
                floorId: selectedFloor.id,
                startNumber: start,
                endNumber: end,
                description: `${selectedFloor.name} odaları`
            };

            const response = await hierarchicalLocationsAPI.bulkCreateRoomsRange(dto);
            setCreatedRooms(response.data);
            alert(`${response.data.length} oda başarıyla oluşturuldu!`);
        } catch (error: any) {
            console.error('Odalar oluşturulamadı:', error);
            alert(error.response?.data || 'Odalar oluşturulamadı!');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            if (currentStep === 2) {
                setSelectedDepartment(null);
                setFloors([]);
            } else if (currentStep === 3) {
                setSelectedFloor(null);
                setCreatedRooms([]);
                setGeneratedQRs({});
            }
        }
    };

    const handleReset = () => {
        setCurrentStep(1);
        setSelectedDepartment(null);
        setSelectedFloor(null);
        setCreatedRooms([]);
        setFloors([]);
        setGeneratedQRs({});
    };

    return (
        <div className="wizard-container">
            {/* Progress Steps */}
            <div className="wizard-progress">
                {WIZARD_STEPS.map((step) => (
                    <div
                        key={step.number}
                        className={`wizard-step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''
                            }`}
                    >
                        <div className="step-number">
                            {currentStep > step.number ? '✓' : step.number}
                        </div>
                        <div className="step-info">
                            <div className="step-title">{step.title}</div>
                            <div className="step-description">{step.description}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Breadcrumb */}
            {(selectedDepartment || selectedFloor) && (
                <div className="wizard-breadcrumb">
                    <span className="breadcrumb-item">🏥 {selectedDepartment?.name || 'Departman'}</span>
                    {selectedFloor && (
                        <>
                            <span className="breadcrumb-separator">›</span>
                            <span className="breadcrumb-item">🏢 {selectedFloor.name}</span>
                        </>
                    )}
                </div>
            )}

            {/* Step Content */}
            <div className="wizard-content">
                {/* Step 1: Department Selection */}
                {currentStep === 1 && (
                    <div className="step-content">
                        <h2>Departman Seçin</h2>

                        <div className="cards-grid">
                            {/* Create New Department Card */}
                            <div className="location-card create-new">
                                <div className="card-icon">➕</div>
                                <h3>Yeni Departman Oluştur</h3>
                                <input
                                    type="text"
                                    value={newDepartmentName}
                                    onChange={(e) => setNewDepartmentName(e.target.value)}
                                    placeholder="Departman adı (örn: Dahiliye)"
                                    className="card-input"
                                    onKeyPress={(e) => e.key === 'Enter' && handleCreateDepartment()}
                                />
                                <button
                                    onClick={handleCreateDepartment}
                                    disabled={!newDepartmentName.trim() || loading}
                                    className="card-button primary"
                                >
                                    Oluştur
                                </button>
                            </div>

                            {/* Existing Departments */}
                            {departments.map((dept) => (
                                <div
                                    key={dept.id}
                                    className="location-card department"
                                    onClick={() => handleSelectDepartment(dept)}
                                >
                                    <div className="card-icon">🏥</div>
                                    <h3>{dept.name}</h3>
                                    <p className="card-meta">
                                        {dept.children?.length || 0} kat
                                    </p>
                                    <button className="card-button">Seç →</button>
                                </div>
                            ))}
                        </div>

                        {departments.length === 0 && !loading && (
                            <div className="empty-state">
                                <p>Henüz departman bulunmuyor. Yukarıdan yeni departman oluşturun.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Floor Selection */}
                {currentStep === 2 && selectedDepartment && (
                    <div className="step-content">
                        <h2>{selectedDepartment.name} - Kat Seçin</h2>

                        <div className="cards-grid">
                            {/* Create New Floor Card */}
                            <div className="location-card create-new">
                                <div className="card-icon">➕</div>
                                <h3>Yeni Kat Ekle</h3>
                                <input
                                    type="number"
                                    value={newFloorNumber}
                                    onChange={(e) => setNewFloorNumber(e.target.value)}
                                    placeholder="Kat numarası (örn: 1)"
                                    className="card-input"
                                    min="1"
                                    onKeyPress={(e) => e.key === 'Enter' && handleCreateFloor()}
                                />
                                <button
                                    onClick={handleCreateFloor}
                                    disabled={!newFloorNumber.trim() || loading}
                                    className="card-button primary"
                                >
                                    Kat Ekle
                                </button>
                            </div>

                            {/* Existing Floors */}
                            {floors.map((floor) => (
                                <div
                                    key={floor.id}
                                    className="location-card floor"
                                >
                                    <div className="card-icon">🏢</div>
                                    <h3>{floor.name}</h3>
                                    <p className="card-meta">
                                        Kat {floor.floorNumber}
                                    </p>
                                    <div className="card-button-group">
                                        <button
                                            className="card-button primary"
                                            onClick={() => handleSelectFloor(floor)}
                                        >
                                            ➕ Yeni Oda Ekle
                                        </button>
                                        <button
                                            className="card-button secondary"
                                            onClick={() => handleViewFloorQRs(floor)}
                                        >
                                            📋 QR'ları Görüntüle
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {floors.length === 0 && !loading && (
                            <div className="empty-state">
                                <p>Bu departmanda henüz kat bulunmuyor. Yukarıdan yeni kat ekleyin.</p>
                            </div>
                        )}

                        <button onClick={handleBack} className="back-button">
                            ← Geri
                        </button>
                    </div>
                )}

                {/* Step 3: Room Creation */}
                {currentStep === 3 && selectedFloor && (
                    <div className="step-content">
                        <h2>{selectedFloor.name} - Oda QR Kodları</h2>

                        {createdRooms.length === 0 && (
                            <div className="room-creation-panel">
                                <div className="input-group">
                                    <label>Oda Numarası Aralığı</label>
                                    <div className="range-inputs">
                                        <input
                                            type="number"
                                            value={roomStartNumber}
                                            onChange={(e) => setRoomStartNumber(e.target.value)}
                                            placeholder="Başlangıç"
                                            min="1"
                                            className="range-input"
                                        />
                                        <span className="range-separator">-</span>
                                        <input
                                            type="number"
                                            value={roomEndNumber}
                                            onChange={(e) => setRoomEndNumber(e.target.value)}
                                            placeholder="Bitiş"
                                            min="1"
                                            className="range-input"
                                        />
                                    </div>
                                    <p className="input-hint">
                                        Örnek: 1-10 arası {parseInt(roomEndNumber) - parseInt(roomStartNumber) + 1 || 0} oda oluşturulacak
                                    </p>
                                </div>

                                <button
                                    onClick={handleCreateRooms}
                                    disabled={loading}
                                    className="create-rooms-button"
                                >
                                    🎯 QR Kodları Oluştur
                                </button>
                            </div>
                        )}

                        {createdRooms.length > 0 && (
                            <div className="created-rooms-list">
                                <h3>✅ QR Kodları ({createdRooms.length})</h3>

                                <div className="bulk-actions">
                                    <button onClick={downloadAllQRCodes} className="bulk-download-button">
                                        📥 Tümünü İndir ({createdRooms.length} QR - ZIP)
                                    </button>
                                </div>

                                <div className="qr-display-grid">
                                    {createdRooms.map((room) => (
                                        <div key={room.id} className="qr-display-card">
                                            <div className="qr-card-header">
                                                <h4>{room.name}</h4>
                                                <span className="qr-code-badge">{room.qrCode}</span>
                                            </div>

                                            <div className="qr-canvas-container">
                                                <canvas
                                                    id={`qr-room-${room.id}`}
                                                    className="qr-canvas-display"
                                                ></canvas>
                                            </div>

                                            <div className="qr-card-actions">
                                                <button
                                                    onClick={() => downloadQRCode(room.name, room.qrCode)}
                                                    className="qr-action-button download"
                                                    disabled={!generatedQRs[room.name]}
                                                >
                                                    ⬇️ İndir
                                                </button>
                                                <button
                                                    onClick={() => printQRCode(room.name, room.qrCode)}
                                                    className="qr-action-button print"
                                                    disabled={!generatedQRs[room.name]}
                                                >
                                                    🖨️ Yazdır
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="success-actions">
                                    <button onClick={handleReset} className="reset-button">
                                        🔄 Yeni QR Seti Oluştur
                                    </button>
                                </div>
                            </div>
                        )}

                        <button onClick={handleBack} className="back-button">
                            ← Geri
                        </button>
                    </div>
                )}
            </div>

            {loading && (
                <div className="wizard-loading">
                    <div className="spinner"></div>
                    <p>Yükleniyor...</p>
                </div>
            )}
        </div>
    );
};

export default HierarchicalQRWizard;
